import React, { useEffect, useMemo, useRef, useState } from 'react';
import Loader from '../../components/general/loader';
import PageError from '../../components/general/error';
import MarketNoAvailable from '../../components/pages/marketPage/noAvailable';
import useFreeNft, { IUseFreeNft } from '../../hooks/freeNft/useFreeNft';
import { useSearchParams } from 'react-router-dom';
import {
  getNftMarketStatus,
  MarketNftStatuses
} from '../../components/pages/marketPage/Interfaces';
import { API_NftDetails } from 'shared';
import MarketPagePC from './PC';
import useWindowSize from '../../hooks/useWindowSize';
import MarketPageTablet from './Tablet';
import MarketPageMobile from './Mobile';
import usePromo, {
  PromoProvider
} from '../../components/pages/marketPage/promoCode/Context';

type NftWithStatus =
  | { nft: undefined }
  | { nft: API_NftDetails; status: MarketNftStatuses };

enum Modes {
  PC,
  TABLET,
  MOBILE
}

const MarketPage = () => {
  const [params] = useSearchParams();
  const initialId = params.get('id');
  const freeNft = useFreeNft(initialId === null ? undefined : initialId);
  const { loading, nft: rawNft, error, noAvailable } = freeNft;

  const nftWithStatus = useMemo<NftWithStatus>(() => {
    if (rawNft) return { nft: rawNft, status: getNftMarketStatus(rawNft) };
    return { nft: rawNft };
  }, [rawNft]);

  if (loading) {
    return <Loader size={120} useAbsoluteWrapper />;
  }
  if (error.status) {
    return <PageError className='pt-100' wording={error.message} />;
  }
  if (noAvailable) {
    return <MarketNoAvailable />;
  }
  if (nftWithStatus.nft) {
    const { nft, status } = nftWithStatus;
    return (
      <PromoProvider>
        <Content nft={nft} status={status} freeNft={freeNft} />
      </PromoProvider>
    );
  }
  return null;
};

interface ContentProps {
  nft: API_NftDetails;
  status: MarketNftStatuses;
  freeNft: IUseFreeNft;
}

const Content = ({ nft, status, freeNft }: ContentProps) => {
  const { reset } = usePromo();
  const [actionLoading, setActionLoading] = useState(false);
  const [sizeX] = useWindowSize();
  const prevNftId = useRef(nft._id);

  useEffect(() => {
    if (nft._id !== prevNftId.current) {
      reset();
    }
  }, [nft._id]);

  const mode = useMemo<Modes>(() => {
    if (sizeX <= 650) return Modes.MOBILE;
    if (sizeX <= 900) return Modes.TABLET;
    return Modes.PC;
  }, [sizeX <= 650, sizeX <= 900]);

  if (mode === Modes.MOBILE) {
    return (
      <MarketPageMobile
        status={status}
        nft={nft}
        freeNft={freeNft}
        actionLoading={actionLoading}
        setActionLoading={setActionLoading}
      />
    );
  }
  if (mode === Modes.TABLET) {
    return (
      <MarketPageTablet
        status={status}
        nft={nft}
        freeNft={freeNft}
        actionLoading={actionLoading}
        setActionLoading={setActionLoading}
      />
    );
  }
  return (
    <MarketPagePC
      status={status}
      nft={nft}
      freeNft={freeNft}
      actionLoading={actionLoading}
      setActionLoading={setActionLoading}
    />
  );
};

export default MarketPage;
